import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* Widgets */
import { LogDetail, LogFilter } from "../../widgets/Log"

/* REST API */
import { list } from "../../api/Log"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, FilterIcon, EyeIcon, CopyIcon, LogIcon, AlertIcon, WarningIcon, InfoIcon, StudioIcon, LayersIcon, ClockIcon, CalendarIcon } from "../../widgets/Log/LogIcons"

/* Utils */
import { getLevel, getName, formatTime, formatDay, formatAgo, formatRange, isCut } from "../../widgets/Log/LogUtils"

/* Translate */
import { t } from "../../translates/translate"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Moment */
import moment from "moment"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 20

/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400


/* Page Logs */
class Logs extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            stats: null,
            search: "",
            tables: [],
            type: "",
            selected: "all",
            start: null,
            end: null,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search, tables, type, start, end } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list({
            page,
            search,
            type,
            tables: tables.map((item) => item.slug).join(","),
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
        }).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, count: response.data.count, stats: response.data.stats })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, stats: null })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, stats: null })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, stats: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, stats: null })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Apply Filter */
    applyFilter = ({ selected, start, end, tables, type }) => {
        this.setState({ selected, start, end, tables, type }, () => this.apply())
    }

    /* Apply Action — every filter restarts from the first page */
    apply = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/log/1")
            return
        }

        this.load()
    }

    /* Copy Action — a stack trace goes to the clipboard straight from the row */
    copy = (value) => {

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("log.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(value).then(() => {
            Swal.fire(utils.notification(t("log.copied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("log.copyFailed"), "error"))
        })

    }

    /* Count Of The Active Filters — the badge of the filter button */
    getCount = () => {

        /* Fields */
        const { tables, type, selected } = this.state
        let count = 0

        if (tables.length > 0) {
            count = count + 1
        }

        if (type !== "") {
            count = count + 1
        }

        if (selected !== "all") {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Draw Level Icon */
    _icon = (type, size) => {

        /* Fields */
        const level = getLevel(type)

        /* Check Level */
        if (level === "error") {
            return <AlertIcon size={size} />
        }

        if (level === "warning") {
            return <WarningIcon size={size} />
        }

        /* Send Result */
        return <InfoIcon size={size} />
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="log--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("log.search")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="log--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Filter Button */
    _filter = () => {

        /* Fields */
        const { selected, start, end, tables, type } = this.state
        const count = this.getCount()

        return (
            <div
                onClick={() => this._filterModal.open({ selected, start, end, tables, type })}
                className={`log--filter ${count > 0 ? "active" : ""}`}
            >
                <FilterIcon size={15} />
                <span>{t("log.filter")}</span>
                {count > 0 && <div className="log--filter-count">{count}</div>}
            </div>
        )
    }

    /* Draw Summary — what the current filter really holds */
    _summary = () => {

        /* Fields */
        const { count, stats } = this.state

        /* Check Stats */
        if (!stats) {
            return null
        }

        return (
            <div className="log--summary">

                <div className="log--summary-item">
                    <div className="log--summary-title">
                        <LogIcon size={12} />
                        {t("log.total")}
                    </div>
                    <div className="log--summary-value">{count}</div>
                </div>

                <div className="log--summary-item">
                    <div className="log--summary-title">
                        <LayersIcon size={12} />
                        {t("log.games")}
                    </div>
                    <div className="log--summary-value">{stats.tables}</div>
                </div>

                <div className="log--summary-item">
                    <div className="log--summary-title">
                        <CalendarIcon size={12} />
                        {t("log.range")}
                    </div>
                    <div className="log--summary-value small">{formatRange(stats.first, stats.last)}</div>
                </div>

                <div className="log--summary-item">
                    <div className="log--summary-title">
                        <ClockIcon size={12} />
                        {t("log.lastRecord")}
                    </div>
                    <div className="log--summary-value small">{stats.last ? formatAgo(stats.last) : "-"}</div>
                </div>

            </div>
        )
    }

    /* Draw Actions */
    _actions = (item) => {

        return (
            <>
                <div onClick={() => this._detailModal.open({ id: item.id })} className="log--action" title={t("log.detailTitle")}>
                    <EyeIcon />
                </div>

                <div onClick={() => this.copy(item.message)} className="log--action" title={t("log.copyMessage")}>
                    <CopyIcon size={15} />
                </div>
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="log--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="log--page">

                {/* Draw Summary */}
                {this._summary()}

                {/* Head */}
                <div className="log--table-header">
                    <div className="log--column log--col-number">#</div>
                    <div className="log--column log--col-game">{t("log.game")}</div>
                    <div className="log--column log--col-message">{t("log.message")}</div>
                    <div className="log--column log--col-date">{t("log.date")}</div>
                    <div className="log--column log--col-actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="log--empty">
                        <LogIcon size={40} />
                        <span>{search !== "" || this.getCount() > 0 ? t("log.nothingFound") : t("log.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div onClick={() => this._detailModal.open({ id: item.id })} className="log--row" key={`${index}`}>

                        <div className="log--column log--col-number">{(page - 1) * SIZE + index + 1}</div>

                        <div className="log--column log--col-game">

                            <div className={`log--level ${getLevel(item.type)}`}>{this._icon(item.type, 18)}</div>

                            <div className="log--game-text">
                                <div className="log--game-name">{getName(item)}</div>
                                <div className="log--game-note">
                                    <span className={`log--type ${getLevel(item.type)}`}>{item.type}</span>
                                    {item.table && item.tableName ? <span className="log--slug">{item.table}</span> : null}
                                </div>
                            </div>

                        </div>

                        <div className="log--column log--col-message">

                            <div className="log--text">{utils.checkValue(item.message, t("log.noMessage"))}</div>

                            {(item.studioName || isCut(item)) &&
                                <div className="log--text-note">
                                    {item.studioName &&
                                        <span className="log--studio">
                                            <StudioIcon size={11} />
                                            {item.studioName}
                                        </span>
                                    }
                                    {isCut(item) && <span className="log--cut">{t("log.cut")}</span>}
                                </div>
                            }

                        </div>

                        <div className="log--column log--col-date">
                            <div className="log--date">{formatDay(item.createdAt)}</div>
                            <div className="log--time">{formatTime(item.createdAt)}</div>
                            <div className="log--ago">{formatAgo(item.createdAt)}</div>
                        </div>

                        <div onClick={(event) => event.stopPropagation()} className="log--column log--col-actions">{this._actions(item)}</div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => navigate(`/log/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header log--header">

                    <h1>{t("log.title")}</h1>

                    {status === "ok" &&
                        <div className="log--tools">

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw Filter Button */}
                            {this._filter()}

                        </div>
                    }

                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Detail Modal */}
                <LogDetail ref={(ref) => this._detailModal = ref} />

                {/* Draw Filter Modal */}
                <LogFilter apply={this.applyFilter} ref={(ref) => this._filterModal = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Logs))
